import React from 'react';
import classNames from 'classnames';
import { localeURI } from '../../utils/util';
import welcome1 from '../images/platform/twitch.png';
import twitter_logo from '../images/platform/twitter.png';
// import tiktok_logo from '../images/white_tiktok.png';

import steam_logo from '../images/platform/steam.png';
import './ConnectPlatforms.scss';
import check_logo from './check_logo.png';
import youtube_logo from '../images/platform/youtube.png';
import { simpleNotification } from '../../utils/notifications';
// import facebook_logo from '../images/facebook-logo-white.png';




class ConnectPlatforms extends React.Component {
  constructor(props) {
    super(props);
    this.props = props;
    this.state = {
      connected_hovered_twitch: false,
      connected_hovered_twitter: false,
      connected_hovered_youtube: false,
      connected_hovered_steam: false,
    };
  }

  initiateOAuth = async (oauthEndpoint) => {
    const token = this.props?.auth?.token || null;
    const endpoint = String(oauthEndpoint || '');

    const setTempOauthJwtCookie = () => {
      try {
        if (!token) return;
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `noiz_oauth_jwt=${encodeURIComponent(token)}; Max-Age=120; Path=/api/v1/auth; SameSite=Lax${secure}`;
      } catch (e) {
        // ignore
      }
    };

    const redirectToSignIn = () => {
      const redirectTo = window.location.pathname + (window.location.search || '');
      window.location.href = `/sign-in?redirectto=${encodeURIComponent(redirectTo)}`;
    };

    const clearLocalAuth = () => {
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } catch (e) {
        // ignore
      }
    };

    let lastStartError = null;
    const tryTokenDrivenStart = async (startEndpoint) => {
      try {
        const resp = await fetch(localeURI(startEndpoint), {
          method: 'GET',
          headers: {
            Authorization: `JWT ${token}`,
          },
          credentials: 'same-origin',
        });
        if (!resp.ok) {
          let body = null;
          try {
            body = await resp.clone().json();
          } catch (e) {
            // ignore non-json
          }

          const forcedLogout = body?.code === 'E_FORCED_LOGOUT' || body?.error === 'E_FORCED_LOGOUT';
          if (resp.status === 400 && forcedLogout) {
            clearLocalAuth();
            redirectToSignIn();
            return true; // stop further handling
          }

          lastStartError = body?.message || body?.error || `HTTP ${resp.status}`;
          throw new Error(lastStartError);
        }
        const body = await resp.json();
        const url = body?.url;
        if (!url) throw new Error('Missing url in response');
        window.location.href = url;
        return true;
      } catch (e) {
        lastStartError = lastStartError || e?.message || 'OAuth start failed';
        return false;
      }
    };

    // If the user isn't authenticated, don't navigate to the public `/api/v1/auth/twitch` endpoint
    // (it relies on session cookies and will return "User not authenticated" under tunnels/proxies).
    if (!token && (endpoint.includes('/api/v1/auth/twitch') || endpoint.includes('/api/v1/auth/steam'))) {
      redirectToSignIn();
      return;
    }

    // Prefer token-driven start for Twitch/Steam to avoid relying on session cookies.
    const resolveStartEndpoint = (raw, from, to) => {
      try {
        // Handle both relative and absolute urls.
        const u = new URL(raw, window.location.origin);
        if (u.pathname.startsWith(from)) {
          u.pathname = u.pathname.replace(from, to);
          return u.pathname + u.search;
        }
        return null;
      } catch (e) {
        if (raw.startsWith(from)) return raw.replace(from, to);
        return null;
      }
    };

    if (token) {
      const twitchStart = resolveStartEndpoint(endpoint, '/api/v1/auth/twitch', '/api/v1/auth/twitch/start');
      if (twitchStart) {
        const started = await tryTokenDrivenStart(twitchStart);
        if (started) return;
      }

      const steamStart = resolveStartEndpoint(endpoint, '/api/v1/auth/steam', '/api/v1/auth/steam/start');
      if (steamStart) {
        const started = await tryTokenDrivenStart(steamStart);
        if (started) return;
      }
    }

    // Fallback:
    // If the token-driven start fails (tunnel/proxy/DNS issues), fall back to the legacy endpoint.
    // This keeps the old behavior working in environments where sessions are still intact.
    if (endpoint.includes('/api/v1/auth/twitch') || endpoint.includes('/api/v1/auth/steam')) {
      if (token) {
        // Navigation to the legacy endpoint won't carry the Authorization header.
        // Set a short-lived cookie so backend can still identify the user.
        setTempOauthJwtCookie();
        window.location.href = localeURI(endpoint);
        return;
      }

      simpleNotification({
        level: 'warning',
        title: '',
        message: `Unable to start authorization (${lastStartError || 'unknown error'}). Please refresh and try again.`,
      });
      return;
    }

    // For other providers, keep existing direct navigation behavior.
    window.location.href = localeURI(endpoint);
  }

  isAlreadyConnected = (account) => {
    return account && (account.id || account.username);
  }

  handleDisconnect = (platform) => {
    const { token, user } = this.props.auth;

    // Guard: ensure token exists before attempting disconnect
    if (!token) {
      console.error('Cannot disconnect: token is undefined. User may not be authenticated.');
      return;
    }

    const disconnectAction = this.props[`disconnect${platform.charAt(0).toUpperCase() + platform.slice(1)}`];

    if (disconnectAction) {
      disconnectAction(token, user);
    } else {
      console.warn(`disconnect${platform} prop not provided to ConnectPlatforms`);
    }
  }

  saveOAuthReturnTo = (platform) => {
    if (platform !== 'twitch' && platform !== 'youtube') return;
    try {
      sessionStorage.setItem('oauthReturnTo', '/connect-accounts');
    } catch (e) {
      // ignore
    }
  }

  getPlatformConfig = () => {
    const { user } = this.props.auth;
    const oauthEndpointOverrides = this.props.oauthEndpointOverrides || {};
    const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';

    const buildDefaultCallback = (platform) => {
      if (!origin) return '';
      return `${origin}/connect-accounts/${platform}`;
    };

    const normalizeAccount = (account) => {
      // Standardize disconnected accounts to { username: null, id: null }
      if (!account || (account.username === null && account.id === null)) {
        return { username: null, id: null };
      }
      return account;
    };

    return [
      {
        key: 'twitch',
        logo: welcome1,
        platformName: 'Twitch',
        username: normalizeAccount(user.connectedAccounts.twitch)?.username,
        id: normalizeAccount(user.connectedAccounts.twitch)?.id,
        hoverKey: 'connected_hovered_twitch',
        oauthEndpoint:
          oauthEndpointOverrides.twitch ||
          (origin ? `/api/v1/auth/twitch?callbackURL=${encodeURIComponent(buildDefaultCallback('twitch'))}` : '/api/v1/auth/twitch'),
      },
      {
        key: 'twitter',
        logo: twitter_logo,
        platformName: 'Twitter',
        username: normalizeAccount(user.connectedAccounts.twitter)?.username,
        id: normalizeAccount(user.connectedAccounts.twitter)?.id,
        hoverKey: 'connected_hovered_twitter',
        oauthEndpoint:
          oauthEndpointOverrides.twitter || '/api/v1/auth/twitter',
      },
      {
        key: 'steam',
        logo: steam_logo,
        platformName: 'Steam',
        username: normalizeAccount(user.connectedAccounts.steam)?.username,
        id: normalizeAccount(user.connectedAccounts.steam)?.id,
        hoverKey: 'connected_hovered_steam',
        oauthEndpoint:
          oauthEndpointOverrides.steam ||
          (origin ? `/api/v1/auth/steam?callbackURL=${encodeURIComponent(buildDefaultCallback('steam'))}` : '/api/v1/auth/steam'),
      },
      {
        key: 'youtube',
        logo: youtube_logo,
        platformName: 'YouTube',
        username: normalizeAccount(user.connectedAccounts.youtube)?.username,
        id: normalizeAccount(user.connectedAccounts.youtube)?.id,
        hoverKey: 'connected_hovered_youtube',
        oauthEndpoint:
          oauthEndpointOverrides.youtube ||
          (origin ? `/api/v1/auth/youtube?callbackURL=${encodeURIComponent(buildDefaultCallback('youtube'))}` : '/api/v1/auth/youtube'),
      },
    ];
  }

  getImageStyle = (platformKey) => {
    return { objectFit: 'contain' };
  }

  renderPlatformSquare = (platform) => {
    const connected = this.isAlreadyConnected({ id: platform.id, username: platform.username });
    const hover = this.state[platform.hoverKey];

    const squareClass = classNames(
      'ConnectPlatforms--square',
      `ConnectPlatforms--${platform.key}`,
      { connected }
    );

    return (
      <div
        key={platform.key}
        onClick={() => {
          if (connected) {
            this.handleDisconnect(platform.key);
          } else {
            this.saveOAuthReturnTo(platform.key);
            this.initiateOAuth(platform.oauthEndpoint);
          }
        }}
        className={squareClass}
        onMouseOver={() => this.setState({ [platform.hoverKey]: true })}
        onMouseLeave={() => this.setState({ [platform.hoverKey]: false })}
        aria-hidden
      >
        {connected && !hover && (
          <div className="ConnectPlatforms--check">
            <img className="check_icon" src={check_logo} alt="Connected" />
          </div>
        )}
        <div className="ConnectPlatforms--inner">
          <div className="ConnectPlatforms--inner1">
            <img
              className="responsive"
              src={platform.logo}
              alt={`${platform.platformName} Logo`}
              style={this.getImageStyle(platform.key)}
            />
          </div>
          <div className="ConnectPlatforms--inner2">
            {!connected && (
              <button className="ConnectPlatforms--disconnected">Connect</button>
            )}
            {connected && (
              <>
                <div className="ConnectPlatforms--username">
                  {platform.username}
                </div>
                <button className="ConnectPlatforms--disconnect-btn">
                  Disconnect
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const platforms = this.getPlatformConfig();

    return (
      <div className="ConnectPlatforms">
        <div className="ConnectPlatforms--squares">
          {platforms.map(platform => this.renderPlatformSquare(platform))}
        </div>
      </div>
    );
  }
}

ConnectPlatforms.propTypes = {
  // auth: PropTypes.object.isRequired,
  // disconnectTwitch: PropTypes.func.isRequired,
  // disconnectYoutube: PropTypes.func.isRequired,
  // disconnectTwitter: PropTypes.func.isRequired,
  // disconnectSteam: PropTypes.func.isRequired,
};

export default ConnectPlatforms;
